import * as React from "react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import EditIcon from "@mui/icons-material/Edit";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { useAppSelector } from "app/hooks";
import CheckboxFormField from "components/checkbox-form-field";
import RadioGroupFormField from "components/radio-group-form-field";
import { RadioGroupOption } from "components/radio-group-form-field/radio-group-form-field.component";
import CallPatientLink from "components/suno-dialer/call-patient/call-patient-link.component";
import {
  APPOINTMENT_STATUS_DRAFT,
  NOTIFICATION_METHOD_EMAIL,
  NOTIFICATION_METHOD_SMS
} from "constants/appointments";
import {
  COMMUNICATION_METHOD_TYPES,
  PHONE_TYPE_MOBILE
} from "constants/patients";
import { selectCurrentPractice } from "features/auth/auth-slice";
import SelectIntakeForm from "features/scheduling/appointment-form/fields/select-intake-form.component";
import { useGetAppointmentTypeQuery } from "services/appointment-types";
import { useGetIntakeFormRequestsQuery } from "services/onboarding-requests";
import type { Patient } from "services/types/patients.type";
import { formatPhoneNumber } from "utils/common";

import { AppointmentFormValues } from "../appointment-form.component";
import IntakeFormsCount from "./intake-forms-count.component";
import UpdatePatientEmailModal from "./update-patient-email-modal.components";
import UpdatePatientPhoneModal from "./update-patient-phone-modal.components";
import AppointmentUpdatePatientPreferredCommunicationMethodModal from "./update-patient-preferred-communication-method-modal.components";

type NotificationMethodFieldProps = {
  patient: Patient & { id: number };
};

const NotificationsAndIntakeFormsField = memo(
  ({ patient }: NotificationMethodFieldProps) => {
    const currentPractice = useAppSelector(selectCurrentPractice);

    const appointmentCommunicationsEnabled = Boolean(
      currentPractice?.enable_appointment_communications ?? true
    );

    const [updatePhoneIsOpen, setUpdatePhoneIsOpen] = useState(false);
    const [updateEmailIsOpen, setUpdateEmailIsOpen] = useState(false);
    const [
      updatePreferredCommunicationMethodIsOpen,
      setUpdatePreferredCommunicationMethodIsOpen
    ] = useState(false);

    const togglePhoneModal = useCallback(
      () => setUpdatePhoneIsOpen((prev) => !prev),
      []
    );
    const toggleEmailModal = useCallback(
      () => setUpdateEmailIsOpen((prev) => !prev),
      []
    );
    const togglePreferredCommunicationMethodModal = useCallback(
      () => setUpdatePreferredCommunicationMethodIsOpen((prev) => !prev),
      []
    );

    const { control, watch, setValue } =
      useFormContext<AppointmentFormValues>();

    const shouldNotify = watch("should_notify");
    const id = watch("id");
    const shouldCreateIntakeFormRequest = watch(
      "should_create_intake_form_request"
    );
    const appointmentType = watch("appointment_type");

    const hasEmail = Boolean(patient?.email);
    const hasPhone = Boolean(
      patient?.phone &&
        Number(patient?.phone_type ?? PHONE_TYPE_MOBILE) === PHONE_TYPE_MOBILE
    );

    const doNotEmail = Boolean(patient?.do_not_email);
    const doNotMessage = Boolean(patient?.do_not_message);

    const noNotificationMethodsAvailable = !hasEmail && !hasPhone;
    const effectivelyNoNotificationMethods =
      noNotificationMethodsAvailable ||
      (doNotEmail && doNotMessage) ||
      (doNotEmail && !hasPhone) ||
      (doNotMessage && !hasEmail);
    const status = watch("status");
    const existingAppointment = Boolean(id);
    const isDraftAppointment = status === APPOINTMENT_STATUS_DRAFT;
    const shouldDisableSendOnboardingForms =
      existingAppointment && !isDraftAppointment;

    const { data: fullAppointmentType } = useGetAppointmentTypeQuery(
      { id: appointmentType?.id as number, query: "{id,intake_forms}" },
      {
        skip:
          !isDraftAppointment ||
          !appointmentType?.id ||
          appointmentType?.intake_forms !== undefined
      }
    );

    const { data: intakeFormsData = [], isLoading: intakeFormsDataIsLoading } =
      useGetIntakeFormRequestsQuery(
        {
          patientId: patient?.id?.toString(),
          appointment: id?.toString()
        },
        { skip: !id || !patient?.id, refetchOnMountOrArgChange: true }
      );

    const requestedIntakeFormsCount = intakeFormsData[0]?.forms_requested_count;
    const intakeFormsNotificationMethod =
      intakeFormsData[0]?.notification_method;

    const intakeFormsWereCreated = requestedIntakeFormsCount > 0;

    useEffect(() => {
      if (effectivelyNoNotificationMethods) {
        setValue("should_notify", false);
        setValue("should_create_intake_form_request", false);
        setValue("intake_form_request.notification_method", null);
        setValue("notification_method", null);
      }
    }, [effectivelyNoNotificationMethods, setValue]);

    useEffect(() => {
      if (requestedIntakeFormsCount) {
        setValue("should_create_intake_form_request", true);
        setValue(
          "intake_form_request.notification_method",
          intakeFormsNotificationMethod
        );
      }
    }, [requestedIntakeFormsCount, setValue, intakeFormsNotificationMethod]);

    useEffect(() => {
      if (shouldCreateIntakeFormRequest && !requestedIntakeFormsCount) {
        let intake_notification_method: number | null = null;

        if (hasEmail && !doNotEmail) {
          intake_notification_method = NOTIFICATION_METHOD_EMAIL;
        } else if (hasPhone && !doNotMessage) {
          intake_notification_method = NOTIFICATION_METHOD_SMS;
        }

        setValue(
          "intake_form_request.notification_method",
          intake_notification_method
        );
      }
    }, [
      shouldCreateIntakeFormRequest,
      hasEmail,
      hasPhone,
      doNotEmail,
      doNotMessage,
      intakeFormsWereCreated,
      requestedIntakeFormsCount,
      setValue
    ]);

    useEffect(() => {
      if (!existingAppointment || isDraftAppointment) {
        const intakeForms =
          (appointmentType?.intake_forms ?? fullAppointmentType?.intake_forms) ?? [];
        setValue("should_create_intake_form_request", intakeForms.length > 0);

        setValue(
          "intake_form_request.intake_form_request_submissions.create",
          intakeForms.map(({ intake_form }) => ({
            intake_form: intake_form.id
          }))
        );
      }
    }, [appointmentType, existingAppointment, isDraftAppointment, fullAppointmentType, setValue]);

    const getNotificationMethodsOptions = (
      isDisabled: boolean,
      hasPhone: boolean,
      hasEmail: boolean,
      doNotMessage: boolean,
      doNotEmail: boolean,
      disabledTooltip: string
    ): RadioGroupOption[] => [
      {
        value: "1",
        label: "SMS",
        disabled: isDisabled || !hasPhone || doNotMessage,
        tooltip: isDisabled
          ? disabledTooltip
          : doNotMessage
            ? "Patient has Do not contact by Text set"
            : !hasPhone
              ? "Patient has no mobile phone number"
              : ""
      },
      {
        value: "2",
        label: "Email",
        disabled: isDisabled || !hasEmail || doNotEmail,
        tooltip: isDisabled
          ? disabledTooltip
          : doNotEmail
            ? "Patient has Do not contact by Email set"
            : !hasEmail
              ? "Patient has no email address"
              : ""
      }
    ];

    const notificationMethodsOptions: RadioGroupOption[] = useMemo(
      () =>
        getNotificationMethodsOptions(
          existingAppointment,
          hasPhone,
          hasEmail,
          doNotMessage,
          doNotEmail,
          "Can not be updated once appointment is created"
        ),
      [hasPhone, hasEmail, doNotMessage, doNotEmail, existingAppointment]
    );

    const intakeFormsNotificationMethodsOptions: RadioGroupOption[] = useMemo(
      () =>
        getNotificationMethodsOptions(
          intakeFormsWereCreated,
          hasPhone,
          hasEmail,
          doNotMessage,
          doNotEmail,
          "Can not be updated once intake forms were created"
        ),
      [hasPhone, hasEmail, doNotMessage, doNotEmail, intakeFormsWereCreated]
    );

    const sendOnboardingFormsTooltip = useMemo(() => {
      if (shouldDisableSendOnboardingForms) {
        return "To send onboarding forms after an appointment is created, go to the patient chart → Onboarding Forms → New Form Request.";
      }
      if (appointmentCommunicationsEnabled && effectivelyNoNotificationMethods) {
        return "Patient has no notification methods available";
      }
      if (!patient?.birthdate) {
        return "Patient's date of birth is required to create form submission requests";
      }
      return "";
    }, [
      shouldDisableSendOnboardingForms,
      appointmentCommunicationsEnabled,
      effectivelyNoNotificationMethods,
      patient?.birthdate
    ]);

    return (
      <Stack gap={1}>
        {patient && appointmentCommunicationsEnabled && (
          <>
            <Stack flexDirection="row" alignItems="center" gap={0.5} flexWrap="wrap">
              <Typography>
                Preferred Communication Method:{" "}
                {COMMUNICATION_METHOD_TYPES.getFromCode(
                  patient?.preferred_communication_method
                ) || "-"}
              </Typography>

              <IconButton
                onClick={togglePreferredCommunicationMethodModal}
                size="small"
                sx={{ padding: 0.5 }}
              >
                <EditIcon sx={{ width: 14, height: 14 }} />
              </IconButton>

              <AppointmentUpdatePatientPreferredCommunicationMethodModal
                patient={patient}
                isOpen={updatePreferredCommunicationMethodIsOpen}
                onCancel={togglePreferredCommunicationMethodModal}
              />

              {doNotEmail && (
                <Chip
                  label="Do not contact by Email"
                  size="small"
                  color="warning"
                />
              )}
              {doNotMessage && (
                <Chip
                  label="Do not contact by Text"
                  size="small"
                  color="warning"
                />
              )}
            </Stack>

            <Stack direction="row" gap={3}>
              <Stack flexDirection="row" alignItems="center" gap={0.5}>
                <Typography>
                  Patient Mobile:{" "}
                  {hasPhone ? (
                    <CallPatientLink
                      patient={patient}
                      phoneNumber={patient?.phone}
                    >
                      <Link
                        target="_top"
                        rel="noopener noreferrer"
                        href={`tel:${patient?.phone}`}
                      >
                        {formatPhoneNumber(patient?.phone)}
                      </Link>
                    </CallPatientLink>
                  ) : (
                    "Not found"
                  )}
                </Typography>

                <Tooltip title="Update Primary Phone">
                  <IconButton
                    onClick={togglePhoneModal}
                    size="small"
                    sx={{ padding: 0.5 }}
                  >
                    <EditIcon sx={{ width: 14, height: 14 }} />
                  </IconButton>
                </Tooltip>

                <UpdatePatientPhoneModal
                  isOpen={updatePhoneIsOpen}
                  patient={patient}
                  onCancel={togglePhoneModal}
                />
              </Stack>

              <Stack flexDirection="row" alignItems="center" gap={0.5}>
                <Typography>
                  Patient Email:{" "}
                  {hasEmail ? (
                    <Link
                      target="_top"
                      rel="noopener noreferrer"
                      href={`mailto:${patient?.email}`}
                    >
                      {patient?.email}
                    </Link>
                  ) : (
                    "Not found"
                  )}
                </Typography>

                <Tooltip title="Update Primary Email">
                  <IconButton
                    onClick={toggleEmailModal}
                    size="small"
                    sx={{ padding: 0.5 }}
                  >
                    <EditIcon sx={{ width: 14, height: 14 }} />
                  </IconButton>
                </Tooltip>

                <UpdatePatientEmailModal
                  isOpen={updateEmailIsOpen}
                  patient={patient}
                  onCancel={toggleEmailModal}
                />
              </Stack>
            </Stack>
          </>
        )}
        <Stack width="100%" flexDirection="row" justifyContent="space-between">
          {appointmentCommunicationsEnabled && (
            <Tooltip
              title={
                effectivelyNoNotificationMethods
                  ? "Patient has no notification methods available"
                  : ""
              }
            >
              <FormControlLabel
                sx={{
                  whiteSpace: "pre-line"
                }}
                label="Send Appointment Notifications"
                disabled={effectivelyNoNotificationMethods}
                control={
                  <CheckboxFormField
                    disabled={effectivelyNoNotificationMethods}
                    testId="should-notify-checkbox"
                    name="should_notify"
                    control={control}
                  />
                }
              />
            </Tooltip>
          )}

          {appointmentCommunicationsEnabled &&
            !effectivelyNoNotificationMethods &&
            shouldNotify && (
              <RadioGroupFormField
                name="notification_method"
                label=""
                row
                options={notificationMethodsOptions}
                control={control}
                sx={{ width: "max-content" }}
                disabled={existingAppointment}
              />
            )}
        </Stack>

        <Stack>
          <Stack
            width="100%"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Stack>
              <Tooltip title={sendOnboardingFormsTooltip}>
                <FormControlLabel
                  sx={{
                    whiteSpace: "pre-line"
                  }}
                  label="Send Onboarding Forms"
                  disabled={
                    shouldDisableSendOnboardingForms ||
                    !patient?.birthdate ||
                    effectivelyNoNotificationMethods ||
                    intakeFormsDataIsLoading ||
                    requestedIntakeFormsCount > 0
                  }
                  control={
                    <CheckboxFormField
                      disabled={
                        shouldDisableSendOnboardingForms ||
                        !patient?.birthdate ||
                        effectivelyNoNotificationMethods ||
                        intakeFormsDataIsLoading ||
                        requestedIntakeFormsCount > 0
                      }
                      testId="should-notify-checkbox"
                      name="should_create_intake_form_request"
                      control={control}
                    />
                  }
                />
              </Tooltip>
              <IntakeFormsCount
                patientId={patient.id}
                form={intakeFormsData[0]}
                sx={{ paddingLeft: 4 }}
              />
            </Stack>

            {shouldCreateIntakeFormRequest && (
              <RadioGroupFormField
                name="intake_form_request.notification_method"
                label=""
                row
                disabled={
                  intakeFormsDataIsLoading || requestedIntakeFormsCount > 0
                }
                options={intakeFormsNotificationMethodsOptions}
                control={control}
                sx={{ width: "max-content" }}
              />
            )}
          </Stack>

          {!intakeFormsWereCreated && shouldCreateIntakeFormRequest && (
            <SelectIntakeForm
              control={control}
              name="intake_form_request.intake_form_request_submissions.create"
              is_active
              required
            />
          )}
        </Stack>
      </Stack>
    );
  }
);

export default NotificationsAndIntakeFormsField;
